import React, { useState, useEffect, useCallback } from "react";
import {
    Collapse,
    Form,
    Input,
    Select,
    DatePicker,
    Radio,
    Button,
    Row,
    Col,
    message,
    Tooltip,
    Alert,
    InputNumber,
    ConfigProvider,
    Checkbox
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import "./TrafikIhbarBildirimPage.css";
import { useHasar } from "../HasarContext";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import InputMask from "react-input-mask";
import getConfig from "../config";
import trTR from 'antd/locale/tr_TR';
import dayjs from "dayjs";
import 'dayjs/locale/tr';
import { useApiLogger } from "../hooks/useUIEventLogger";

dayjs.locale('tr');

// Set global message config for position
message.config({
    top: 24,
    right: 24,
    duration: 3,
    maxCount: 3,
});

const { Panel } = Collapse;
const { Option } = Select;

const turkishSearch = (input, option) => {
    const normalize = (str) => {
        return (str || '').toString()
            .toUpperCase()
            .replace(/İ/g, 'I')
            .replace(/Ş/g, 'S')
            .replace(/Ğ/g, 'G')
            .replace(/Ü/g, 'U')
            .replace(/Ö/g, 'O')
            .replace(/Ç/g, 'C');
    };

    const text = (option?.children || option?.label || '').toString();
    return normalize(text).indexOf(normalize(input)) >= 0;
};

const fetchSigortaliBilgisi = async (policeNo, token) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/SigortaliBilgiGetir`,
        {
            method: "POST",
            headers:
            {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: policeNo
        }
    );
    if (!response.ok) throw new Error("Sigortalı bilgisi alınamadı");
    return await response.json();
};

const fetchTamirhaneBilgisi = async (token, tamirhaneKod) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/TamirhaneBilgiGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ tamirhaneKod })
        }
    );
    if (!response.ok) throw new Error("Tamirhane bilgisi alınamadı");
    return await response.json();
};

const fetchTramerBilgi = async (token, requestData) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/TramerBilgiGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(requestData)
        }
    );
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Tramer bilgisi alınamadı");
    }
    return await response.json();
};

const fetchKimlikSorgula = async (token, kimlikTipi, kimlikNo) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/KimlikSorgula`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ kimlikTipi, kimlikNo })
        }
    );
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Kimlik bilgisi alınamadı");
    }
    return await response.json();
};

const sendSmsVerification = async (token, gsmNo) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/SmsGonder`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ gsmNo })
        }
    );
    if (!response.ok) throw new Error("SMS gönderilemedi");
    return await response.json();
};

const verifySmsCode = async (token, gsmNo, dogrulamaKodu) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/SmsDogrulamaYap`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ gsmNo, dogrulamaKodu })
        }
    );
    if (!response.ok) throw new Error("SMS doğrulaması yapılamadı");
    return await response.json();
};

const submitTrafikIhbar = async (token, data, logApiCall, logApiSuccess, logApiError) => {

    const config = getConfig();
    const endpoint = `${config.apiBaseUrl}/Hasar/HasarDosyaGiris`;
    const callId = logApiCall(endpoint, 'POST', {
        formType: 'TRAFIK_IHBAR_BILDIRIM'
    });
    const response = await fetch(endpoint, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "accept": "*/*",
            timeout: 300000 // 5 minutes in milliseconds
        },
        body: JSON.stringify(data)
    }
    );
    const responseText = await response.text();

    if (!response.ok) {
        // 401 Unauthorized hatası için özel mesaj
        if (response.status === 401) {
            logApiError(callId, endpoint, "401: Unauthorized - Oturum süresi doldu", response.status, {
                success: false,
                request: JSON.stringify(data),
                response: responseText
            });
            throw new Error("401: Unauthorized - Oturum süresi doldu");
        }

        try {
            // JSON parse etmeyi dene
            const errorJson = JSON.parse(responseText);
            const errorMessage = errorJson.error || errorJson.message || responseText;

            logApiError(callId, endpoint, "Girilen bilgiler hatalı", response.status, {
                success: false,
                request: JSON.stringify(data),
                response: errorJson
            });

            throw new Error(errorMessage + "_" + callId);
        } catch (parseError) {
            // JSON değilse direkt text olarak kullan
            if (parseError.message.includes("401")) {
                throw parseError;
            }
            throw new Error(responseText + "_" + callId);
        }
    }

    const responseData = JSON.parse(responseText);

    logApiSuccess(callId, endpoint, {
        success: true,
        request: JSON.stringify(data),
        response: responseData
    });

    return responseData;
};

const fetchIlListesi = async (token) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/HasarIlGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );
    if (!response.ok) throw new Error("İl listesi alınamadı");
    return await response.json();
};

const fetchIlceListesi = async (token, ilKodu, tip) => {
    if (ilKodu == null || ilKodu == "") {
        return null;
    }
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/HasarIlceGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ ilKodu: ilKodu.toString(), tip: tip })
        }
    );
    if (!response.ok) throw new Error("İlçe listesi alınamadı");
    return await response.json();
};

// Trafik hasar nedenleri
const hasarNedenleri = [
    { HASAR_KOD: "136", ACIKLAMA: "MADDİ SORUMLULUK", TeminatKodu: "XYZ" },
];

// Kaza tipi ve teminat kodu mapping
const kazaTipleri = [
    { KOD: "1", ACIKLAMA: "İkili Kaza", TeminatKodu: "TTA" },
    { KOD: "2", ACIKLAMA: "Zincirleme", TeminatKodu: "TMH" },
];

// Hasar oluş nedenleri ve mağdur türü mapping
const hasarOlusNedenleri = [
    { KOD: "1", ACIKLAMA: "Araç Çarpışması", MagdurTuru: "Araç" },
    { KOD: "2", ACIKLAMA: "Araç Dışında Sabit Bir Cisme Çarpma", MagdurTuru: "Mal" },
    { KOD: "3", ACIKLAMA: "Hayvana Çarpma", MagdurTuru: "Mal" },
    { KOD: "4", ACIKLAMA: "İnsana Çarpma", MagdurTuru: "Can" },
];

// İhbar eden tipleri
const ihbarEdenTipleri = [
    { KOD: "1", ACIKLAMA: "Sigortalı" },
    { KOD: "2", ACIKLAMA: "Mağdur" },
    { KOD: "3", ACIKLAMA: "Sigortalı Yakını" },
    { KOD: "4", ACIKLAMA: "Acente" },
    { KOD: "5", ACIKLAMA: "Tamirhane" },
    { KOD: "6", ACIKLAMA: "Eksper" },
    { KOD: "7", ACIKLAMA: "Avukat" },
    { KOD: "8", ACIKLAMA: "Hizmet Sunucuları" },
    { KOD: "9", ACIKLAMA: "Vekil" },
    { KOD: "10", ACIKLAMA: "Takipçi Firma" },
    { KOD: "11", ACIKLAMA: "Sigorta Şirketi" },
    { KOD: "12", ACIKLAMA: "Mahkeme(Dava İhbarı)" },
    { KOD: "13", ACIKLAMA: "Bölge Müdürlükleri" },
];

// İhbar eden kimlik tipleri
const ihbarEdenKimlikTipleri = [
    { KOD: "1", ACIKLAMA: "TC Kimlik No" },
    { KOD: "2", ACIKLAMA: "Vergi" },
    { KOD: "3", ACIKLAMA: "Pasaport" },
    { KOD: "4", ACIKLAMA: "Yabancı Kimlik" },
    { KOD: "9", ACIKLAMA: "Kimliksiz" },
];

const fetchAracTurleri = async (token, tarifeKod = "K50") => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/AracTurGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(tarifeKod)
        }
    );
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Araç türleri alınamadı");
    }
    return await response.json();
};

function TrafikIhbarBildirimPage() {
    const { token, servisTipi, policeNo, setIhbarResponse } = useHasar();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { logApiCall, logApiSuccess, logApiError } = useApiLogger();

    const [teminatKodu, setTeminatKodu] = useState("");
    const [selectedIl, setSelectedIl] = useState();
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [disableKimlikTipi, setDisableKimlikTipi] = useState(false);
    const [disableKimlikNo, setDisableKimlikNo] = useState(false);
    const [disableSurucuAdi, setDisableSurucuAdi] = useState(false);
    const [disableSurucuSoyad, setDisableSurucuSoyad] = useState(false);
    const [disableSurucuCep, setDisableSurucuCep] = useState(false);
    const [disableSurucuEmail, setDisableSurucuEmail] = useState(false);
    const [disableSurucuYas, setDisableSurucuYas] = useState(false);
    const [isMaskedPhone, setIsMaskedPhone] = useState(false);
    const [selectedServisTipi, setSelectedServisTipi] = useState("");
    const [selectedServisIl, setSelectedServisIl] = useState();
    const [isTamirhaneLoaded, setIsTamirhaneLoaded] = useState(false);

    const [magdurTuru, setMagdurTuru] = useState("");
    const [kazaTipi, setKazaTipi] = useState("");
    const [selectedMagdurIl, setSelectedMagdurIl] = useState();
    const [kamuMu, setKamuMu] = useState(false);
    const [isTramerLoading, setIsTramerLoading] = useState(false);
    const [isTramerDataLoaded, setIsTramerDataLoaded] = useState(false);
    const [isKimlikSorgulaLoading, setIsKimlikSorgulaLoading] = useState(false);
    const [aracTurleri, setAracTurleri] = useState([]);
    const [isAracTurleriLoading, setIsAracTurleriLoading] = useState(false);

    // İl ve İlçe state'leri
    const [ilListesiState, setIlListesiState] = useState([]);
    const [hasarIlceListesiState, setHasarIlceListesiState] = useState([]);
    const [servisIlceListesiState, setServisIlceListesiState] = useState([]);
    const [magdurIlceListesiState, setMagdurIlceListesiState] = useState([]);
    const [isIlLoading, setIsIlLoading] = useState(false);
    const [isHasarIlceLoading, setIsHasarIlceLoading] = useState(false);
    const [isServisIlceLoading, setIsServisIlceLoading] = useState(false);
    const [isMagdurIlceLoading, setIsMagdurIlceLoading] = useState(false);

    // SMS Doğrulama state'leri
    const [isSmsVerified, setIsSmsVerified] = useState(false);
    const [showVerificationInput, setShowVerificationInput] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [countdown, setCountdown] = useState(0);
    const [isSendingSms, setIsSendingSms] = useState(false);
    const [isVerifyingSms, setIsVerifyingSms] = useState(false);
    const [canResendSms, setCanResendSms] = useState(true);

    const filteredServisIlceler = servisIlceListesiState;
    const filteredMagdurIlceler = magdurIlceListesiState;

    const [hasarOlusNedeni, setHasarOlusNedeni] = useState("");

    // Kazayı yapan mapping
    const kazayiYapanMap = {
        "Sigortali": 0,
        "Aile": 1,
        "Diger": 2
    };

    // Countdown timer effect
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0 && showVerificationInput) {
            setCanResendSms(true);
        }
    }, [countdown, showVerificationInput]);

    useEffect(() => {
        if (!token) {
            message.error("Oturum süreniz doldu. Lütfen önce giriş yapınız.");
            navigate("/trafik");
            return;
        }
        try {
            const decoded = jwtDecode(token);
            if (decoded.exp * 1000 < Date.now()) {
                message.error("Oturum süreniz doldu. Lütfen önce giriş yapınız.");
                navigate("/trafik");
                return;
            }
        } catch {
            message.error("Oturum süreniz doldu. Lütfen önce giriş yapınız.");
            navigate("/trafik");
            return;
        }

        // İlk mount'ta form değerlerini set et
        const initialValues = {};
        if (policeNo) {
            initialValues.policeNo = policeNo;
        }

        if (servisTipi === 1) {
            initialValues.servisTipi = "anlasmali";
            setSelectedServisTipi("anlasmali");
        } else if (servisTipi === 0) {
            initialValues.servisTipi = "anlasmasiz";
            setSelectedServisTipi("anlasmasiz");
        }

        form.setFieldsValue(initialValues);

        // Araç türlerini yükle
        loadAracTurleri();

        // İl listesini yükle
        loadIlListesi();
    }, []);

    const loadAracTurleri = async () => {
        setIsAracTurleriLoading(true);
        try {
            const result = await fetchAracTurleri(token);
            if (result && Array.isArray(result)) {
                setAracTurleri(result);
            }
        } catch (error) {
            console.error("Araç türleri yükleme hatası:", error);
            message.warning("Araç türleri yüklenemedi");
        } finally {
            setIsAracTurleriLoading(false);
        }
    };

    const loadIlListesi = async () => {
        setIsIlLoading(true);
        try {
            const result = await fetchIlListesi(token);
            if (result && Array.isArray(result)) {
                setIlListesiState(result);
            }
        } catch (error) {
            console.error("İl listesi yükleme hatası:", error);
            message.warning("İl listesi yüklenemedi");
        } finally {
            setIsIlLoading(false);
        }
    };

    const loadHasarIlceListesi = async (ilKodu) => {
        setIsHasarIlceLoading(true);
        try {
            const result = await fetchIlceListesi(token, ilKodu, "H");
            if (result && Array.isArray(result)) {
                setHasarIlceListesiState(result);
            }
        } catch (error) {
            console.error("Hasar ilçe listesi yükleme hatası:", error);
            message.warning("İlçe listesi yüklenemedi");
        } finally {
            setIsHasarIlceLoading(false);
        }
    };

    const loadServisIlceListesi = async (ilKodu) => {
        setIsServisIlceLoading(true);
        try {
            const result = await fetchIlceListesi(token, ilKodu, "T");
            if (result && Array.isArray(result)) {
                setServisIlceListesiState(result);
            }
        } catch (error) {
            console.error("Servis ilçe listesi yükleme hatası:", error);
            message.warning("Servis ilçe listesi yüklenemedi");
        } finally {
            setIsServisIlceLoading(false);
        }
    };

    const loadMagdurIlceListesi = async (ilKodu) => {
        setIsMagdurIlceLoading(true);
        try {
            const result = await fetchIlceListesi(token, ilKodu);
            if (result && Array.isArray(result)) {
                setMagdurIlceListesiState(result);
            }
        } catch (error) {
            console.error("Mağdur ilçe listesi yükleme hatası:", error);
            message.warning("Mağdur ilçe listesi yüklenemedi");
        } finally {
            setIsMagdurIlceLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        // SMS doğrulama kontrolü
        if (!isSmsVerified) {
            message.error("Lütfen önce cep telefonu numaranızı doğrulayınız");
            return;
        }

        // Yurtdışı kontrolü
        if (values.hasarIl === "0") {
            message.warning({
                content: "Yurtdışında oluşan hasar için dosyanızı Çağrı Merkezini arayarak açtırabilirsiniz",
                duration: 10,
            });
            return;
        }

        // Hasar oluş nedenine göre zorunluluk kontrolleri
        if (values.hasarOlusNedeni === "1" && !isTramerDataLoaded) {
            message.error("Hasar oluş nedeni 'Araç Çarpışması' ise Araç Bilgilerini Sorgula butonuna tıklayarak EGM sorgusu yapmak zorunludur.");
            return;
        }
        if (values.hasarOlusNedeni === "2" && !values.magdurModel) {
            message.error("Hasar oluş nedeni 'Araç Dışında Sabit Bir Cisme Çarpma' ise Model Yılı zorunludur.");
            return;
        }
        if (values.hasarOlusNedeni === "3" && (!values.magdurModel || !values.magdurTuru)) {
            message.error("Hasar oluş nedeni 'Hayvana Çarpma' ise Model Yılı ve Araç Türü zorunludur.");
            return;
        }
        if (values.hasarOlusNedeni === "4" && !values.magdurModel) {
            message.error("Hasar oluş nedeni 'İnsana Çarpma' ise Model Yılı zorunludur.");
            return;
        }

        // Her istekte hata mesajını sıfırla
        setErrorMessage("");
        setIsSubmitting(true);

        try {
            const cleanPhone = (phone) => {
                if (!phone) return "";
                return phone.replace(/[^\d]/g, '').replace(/^0/, '');
            };

            // Dosya türü mapping
            const dosyaTuruMap = {
                "Beyan": 0,
                "Zabit": 2,
                "AnlaşmalıTutanakKTT": 1
            };

            // Kimlik tipi mapping
            const kimlikTipMap = {
                "TcKimlikNo": 1,
                "VergiNo": 2,
                "PasaportNo": 3,
                "YabanciKimlikNo": 4,
                "Kimliksiz": 9
            };

            // İhbar eden kimlik tipi mapping
            const ihbarEdenKimlikTipMap = {
                "1": 1, // TC Kimlik No
                "2": 2, // Vergi
                "3": 3, // Pasaport
                "4": 4, // Yabancı Kimlik
                "9": 9  // Kimliksiz
            };

            const magdurKimlikTipMap = {
                "Bireysel": 66,
                "Yabancı": 89,
                "Ticari": 84
            };

            // Anlaşmasız tamirhane kontrolü
            const isAnlasmasizTamirhane = selectedServisTipi === "anlasmasiz";

            // İkili kaza kontrolü
            const ikiliKazaMi = values.kazaTipi === "1"; // "1" = İkili Kaza

            // API request objesi oluştur
            const requestData = {
                policeNo: parseInt(values.policeNo) || 0,
                hasarNedenKodu: values.hasarNedeni || "",
                teminatKodu: values.teminatKodu || "",
                hasarOlusNedeni: values.hasarOlusNedeni || "",
                hasarTarihVeSaat: values.hasarTarihi
                    ? values.hasarTarihi.format('YYYY-MM-DDTHH:mm:ss') // UTC yerine yerel saat
                    : dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                hasarIlKodu: values.hasarIl || "",
                hasarIlceKodu: values.hasarIlce || "",
                dosyaTuru: dosyaTuruMap[values.dosyaTuru] ?? 0,

                // İhbar yapan bilgileri (İhbar eden = İhbar yapan)
                ihbarYapanTipi: values.ihbarEdenTipi || "",
                ihbarYapanKimlikTip: values.ihbarEdenKimlikTipi || "",
                ihbarYapanKimlikNo: values.ihbarEdenKimlikNo || "",
                ihbarYapanAd: values.ihbarAdSoyad ? values.ihbarAdSoyad.split(' ')[0] : "",
                ihbarYapanSoyad: values.ihbarAdSoyad ? values.ihbarAdSoyad.split(' ').slice(1).join(' ') : "",
                cepTelefon: cleanPhone(values.ihbarCep),
                ihbarYapanEmail: values.ihbarEmail || null,

                //// Sürücü bilgileri
                kazayiYapan: kazayiYapanMap[values.surucuKim] ?? 0,
                surucuAd: values.surucuAdi || "",
                surucuSoyad: values.surucuSoyad || "",
                surucuKimlikNo: values.kimlikNo || "",
                surucuKimlikTip: kimlikTipMap[values.kimlikTipi] ?? 1,
                surucuTelefon: cleanPhone(values.surucuCep),
                surucuEposta: values.surucuEmail || null,
                adSoyad: values.ihbarAdSoyad || "",
                araciKullananYas: parseInt(values.surucuYas) || 30,

                tahminiTazminat: parseFloat(values.tahminiTutar) || 0,

                // Kaza tipi bilgisi
                ikiliMi: ikiliKazaMi,

                // Tamirhane bilgileri
                tamireVerilisTarih: dayjs().format('YYYY-MM-DDTHH:mm:ss'),

                // Mağdur bilgileri
                magdurAd: values.magdurAdi || "",
                magdurSoyad: values.magdurSoyadi || "",
                magdurKimlikNo: values.magdurKimlikNo || "",
                magdurKimlikTipi: magdurKimlikTipMap[values.magdurKimlikTipi] ?? 0,
                magdurCepTelefon: cleanPhone(values.magdurCepTelefonu),
                magdurEmail: values.magdurEmail || null,
                magdurTescilBelgeSeriNo: values.magdurTescilSeri && values.magdurTescilSira
                    ? `${values.magdurTescilSeri}${values.magdurTescilSira}`
                    : "",
                magdurPlaka: values.magdurPlakaIl && values.magdurPlaka
                    ? `${values.magdurPlakaIl.replace(/^0+/, '')}${values.magdurPlaka.replace(/\s/g, '')}`
                    : "",
                magdurMarkaKod: values.magdurMarkaKodu || "",
                magdurMarkaAd: values.magdurMarka || "",
                magdurModelYil: values.magdurModel ? values.magdurModel.toString() : "",
                magdurAracTipi: values.magdurTipi || "",
                magdurAracTuruKod: values.magdurTuruKodu || "",
                magdurAracTuruAd: values.magdurTuru || "",
                magdurMotorNo: values.magdurMotorNo || "",
                magdurSasiNo: values.magdurSasiNo || "",

                // Mağdur hesap bilgileri
                magdurHesapIBAN: values.magdurIban || "",
                magdurHesapSahibiAdi: values.magdurHesapSahibiAdi || "",
                magdurHesapSahibiSoyadi: values.magdurHesapSahibiSoyadi || "",
            };

            requestData.ekBilgi = {
                HakSahibiAdi: values.hakSahibiAdi || "",
                HakSahibiSoyadi: values.hakSahibiSoyadi || "",
                HakSahibiCepTelefonu: cleanPhone(values.hakSahibiCepTelefonu),
                HakSahibiEmail: values.hakSahibiEmail || "",
                HakSahibiAdres: values.hakSahibiAdres || ""
            };

            // Anlaşmalı tamirhane ise sadece servis kodu gönder
            if (!isAnlasmasizTamirhane) {
                requestData.tamirhaneKodu = values.servisKodu || "";
                requestData.tamirhaneAdSoyadUnvan = null;
                requestData.tamirhaneAdres = null;
                requestData.tamirhaneIlKodu = null;
                requestData.tamirhaneIlceKodu = null;
                requestData.tamirhaneTelefon = null;
                requestData.tamirhaneKimlikNo = null;
            } else {
                // Anlaşmasız tamirhane ise tüm bilgileri gönder
                requestData.tamirhaneKodu = null;
                requestData.tamirhaneAdSoyadUnvan = values.servisAdSoyad || "";
                requestData.tamirhaneAdres = values.servisAdres || "";
                requestData.tamirhaneIlKodu = values.servisIlKodu || "";
                requestData.tamirhaneIlceKodu = values.servisIlceKodu || "";
                requestData.tamirhaneTelefon = cleanPhone(values.servisTelefon);
                requestData.tamirhaneKimlikNo = values.servisVergiNo || "";
            }

            // API'ye istek gönder
            const result = await submitTrafikIhbar(token, requestData, logApiCall, logApiSuccess, logApiError);

            // Response'u context'e kaydet
            setIhbarResponse({
                eksperNot: result.eksperNot || "",
                eksperTel: result.eksperTel || "",
                eksperWp: result.eksperWp || "",
                eksperMail: result.eksperMail || "",
                dosyaEksperKodu: result.dosyaEksperKodu || "",
                dosyaEksperi: result.dosyaEksperi || "",
                dosyaNo: result.dosyaNo || null,
                dosyaSorumlusu: result.dosyaSorumlusu || "",
                messages: result.messages || null,
                anlasmasizTamirhane: isAnlasmasizTamirhane
            });

            message.success("Hasar dosyası başarı ile açıldı.");

            // Başarılı işlem sonrası başarı sayfasına yönlendir
            setTimeout(() => {
                navigate("/ihbar/basarili");
            }, 1000);

        } catch (error) {
            console.error("Hata:", error);

            // 401 hatası için özel kontrol
            if (error.message && error.message.includes("401")) {
                message.error("Oturumunuz sona erdi. Lütfen tekrar giriş yapın.");
                navigate("/trafik");
                return;
            }

            let errorMsg = "Trafik ihbar bildirimi kaydedilemedi!";
            let callId = "";

            // Error message'ı parse et ve callId'yi çıkar
            try {
                const errorText = error.message || "";

                // CallId'yi mesajdan ayır (errorMessage_callId formatında)
                if (errorText.includes('_')) {
                    const parts = errorText.split('_');
                    callId = parts[parts.length - 1]; // Son parça callId
                    const messageWithoutCallId = parts.slice(0, -1).join('_'); // Geri kalan mesaj

                    // JSON formatında mı kontrol et
                    if (messageWithoutCallId.includes('{') && messageWithoutCallId.includes('}')) {
                        const jsonMatch = messageWithoutCallId.match(/\{.*\}/);
                        if (jsonMatch) {
                            const errorJson = JSON.parse(jsonMatch[0]);
                            errorMsg = errorJson.error || errorJson.message || messageWithoutCallId;
                        }
                    } else {
                        errorMsg = messageWithoutCallId;
                    }
                } else {
                    errorMsg = errorText;
                }
            } catch (parseError) {
                // Parse edilemezse direkt mesajı kullan
                errorMsg = error.message || errorMsg;
            }
            // CallId varsa mesaja ekle
            const fullErrorMsg = callId
                ? `${errorMsg}\n\nReferans No: ${callId}`
                : errorMsg;

            setErrorMessage(fullErrorMsg);
            message.error({
                content: fullErrorMsg,
                duration: 10, // Daha uzun süre göster
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Seçilen ile göre ilçeleri filtrele ve alfabetik sırala
    const filteredIlceler = hasarIlceListesiState
        .sort((a, b) => (a.ilceAdi || a.ilceAd || '').localeCompare((b.ilceAdi || b.ilceAd || ''), "tr", { sensitivity: "base" }));

    // Sort hasarNedenleri alphabetically by ACIKLAMA
    const sortedHasarNedenleri = [...hasarNedenleri].sort((a, b) =>
        a.ACIKLAMA.localeCompare(b.ACIKLAMA, "tr", { sensitivity: "base" })
    );

    // Tramer bilgi sorgulama handler
    const handleTramerSorgula = async () => {
        try {
            // Form değerini al
            const tescilSeri = form.getFieldValue('magdurTescilSeri');
            const tescilSira = form.getFieldValue('magdurTescilSira');
            const plakaIl = form.getFieldValue('magdurPlakaIl');
            const plaka = form.getFieldValue('magdurPlaka');
            const hasarTarihi = form.getFieldValue('hasarTarihi');
            const isKamu = form.getFieldValue('magdurKamuMu') || false;

            // Zorunlu alanları kontrol et
            if (!tescilSeri || !tescilSira) {
                message.warning("Tescil Belge Seri No zorunludur");
                return;
            }
            if (!plakaIl || !plaka) {
                message.warning("Plaka bilgisi zorunludur");
                return;
            }

            // Plaka numarasındaki gereksiz karakterleri temizle
            const cleanedPlaka = plaka.replace(/[_\-\s]/g, '');

            setIsTramerLoading(true);

            // API isteği için request objesi oluştur
            const requestData = {
                plakaIl: plakaIl,
                plakaNo: cleanedPlaka,
                tescilSeriNo: tescilSira,
                tescilSeriKod: tescilSeri,
                baslamaTarih: dayjs(),
                isKamu: isKamu
            };

            const result = await fetchTramerBilgi(token, requestData);

            // Başarılı sonuç
            message.success("Araç bilgileri başarıyla getirildi");
            // Sonuçları form alanlarına doldur
            if (result) {
                const aracBilgi = result;

                form.setFieldsValue({
                    magdurMarkaKodu: aracBilgi.aracMarkaKodu || "",
                    magdurMarka: aracBilgi.aracMarkaAciklama || "",
                    magdurModelKodu: aracBilgi.aracTipKodu || "",
                    magdurModel: aracBilgi.aracModelYil || "",
                    magdurTipi: aracBilgi.aracTipAciklama || "",
                    magdurTipiKodu: aracBilgi.aracTipKod || "",
                    magdurTuru: aracBilgi.aracCinsi || "",
                    magdurTuruKodu: aracBilgi.aracTarifeGrupKod || "",
                    magdurMotorNo: aracBilgi.aracMotorNo || "",
                    magdurSasiNo: aracBilgi.sasiNo || "",
                });

                // Tramer verisi yüklendikten sonra alanları disable et
                setIsTramerDataLoaded(true);
            }

        } catch (error) {
            console.error("Tramer Sorgulama Hatası:", error);
            message.error(error.message || "Araç bilgileri alınamadı");
            setIsTramerDataLoaded(false);
        } finally {
            setIsTramerLoading(false);
        }
    };

    // Seçilen il değiştiğinde ilçe bilgisini sıfırla
    useEffect(() => {
        if (selectedIl) {
            form.setFieldsValue({ hasarIlce: undefined });
        }
    }, [selectedIl]);

    // Kimlik sorgulama handler
    const handleKimlikSorgula = async (kimlikTipi, kimlikNo) => {
        //if (!kimlikNo || kimlikNo.length < 10) {
        //    return;
        //}

        // Kimlik tipi mapping
        const kimlikTipiMap = {
            "1": "TCKN",    // TC Kimlik No
            "2": "VKN",     // Vergi No
            "4": "YKN"   // Yabancı Kimlik
        };

        const mappedKimlikTipi = kimlikTipiMap[kimlikTipi];
        if (!mappedKimlikTipi) {
            return;
        }

        setIsKimlikSorgulaLoading(true);
        try {
            const result = await fetchKimlikSorgula(token, mappedKimlikTipi, kimlikNo);

            // TC Kimlik için
            if (mappedKimlikTipi === "TCKN" && result) {
                const adSoyad = `${result.adi || ""} ${result.soyadi || ""}`.trim();
                form.setFieldsValue({
                    ihbarAdSoyad: adSoyad
                });
                message.success("Kimlik bilgisi başarıyla getirildi");
            }
            // Yabancı Kimlik için
            else if (mappedKimlikTipi === "YKN" && result) {
                const adSoyad = `${result.adi || ""} ${result.soyadi || ""}`.trim();
                form.setFieldsValue({
                    ihbarAdSoyad: adSoyad
                });
                message.success("Kimlik bilgisi başarıyla getirildi");
            }
            // Vergi No için
            else if (mappedKimlikTipi === "VKN" && result) {
                form.setFieldsValue({
                    ihbarAdSoyad: result.unvan || ""
                });
                message.success("Vergi kimlik bilgisi başarıyla getirildi");
            }

        } catch (error) {
            message.warning("Kimlik bilgisi alınamadı, girilen bilgileri kontrol ediniz.");
            form.setFieldsValue({
                ihbarEdenKimlikNo: ""
            });
        } finally {
            setIsKimlikSorgulaLoading(false);
        }
    };

    // Sürücü kimlik sorgulama handler
    const handleSurucuKimlikSorgula = async (kimlikTipi, kimlikNo) => {
        //if (!kimlikNo || kimlikNo.length < 10) {
        //    return;
        //}

        // Kimlik tipi mapping
        const kimlikTipiMap = {
            "1": "TCKN",    // TC Kimlik No
            "4": "YKN"      // Yabancı Kimlik
        };

        const mappedKimlikTipi = kimlikTipiMap[kimlikTipi];
        if (!mappedKimlikTipi) {
            return;
        }

        setIsKimlikSorgulaLoading(true);
        try {
            const result = await fetchKimlikSorgula(token, mappedKimlikTipi, kimlikNo);

            // TC Kimlik veya Yabancı Kimlik için
            if ((mappedKimlikTipi === "TCKN" || mappedKimlikTipi === "YKN") && result) {
                form.setFieldsValue({
                    surucuAdi: result.adi || "",
                    surucuSoyad: result.soyadi || ""
                });
                message.success("Sürücü kimlik bilgisi başarıyla getirildi");
            }

        } catch (error) {
            console.error("Sürücü Kimlik Sorgulama Hatası:", error);
            message.warning("Sürücü kimlik bilgisi alınamadı");
            form.setFieldsValue({
                kimlikNo: ""
            });
        } finally {
            setIsKimlikSorgulaLoading(false);
        }
    };

    // Mağdur kimlik sorgulama handler
    const handleMagdurKimlikSorgula = async (kimlikTipi, kimlikNo) => {
        //if (!kimlikNo || kimlikNo.length < 10) {
        //    return;
        //}

        // Kimlik tipi mapping
        const kimlikTipiMap = {
            "Bireysel": "TCKN",    // TC Kimlik No
            "Ticari": "VKN", // Vergi No
            "Yabancı": "YKN"       // Yabancı Kimlik
        };

        const mappedKimlikTipi = kimlikTipiMap[kimlikTipi];
        if (!mappedKimlikTipi) {
            return;
        }

        setIsKimlikSorgulaLoading(true);
        try {
            const result = await fetchKimlikSorgula(token, mappedKimlikTipi, kimlikNo);

            // TC Kimlik veya Yabancı Kimlik için
            if ((mappedKimlikTipi === "TCKN" || mappedKimlikTipi === "YKN") && result) {
                form.setFieldsValue({
                    magdurAdi: result.adi || "",
                    magdurSoyadi: result.soyadi || ""
                });
                message.success("Mağdur kimlik bilgisi başarıyla getirildi");
            }
            // Vergi No için
            else if (mappedKimlikTipi === "VKN" && result) {
                const unvan = result.unvan || "";
                // Ünvanı ad ve soyad olarak böl (basit bir yaklaşım)
                const parts = unvan.split(' ');
                const adi = parts[0] || unvan;
                const soyadi = parts.slice(1).join(' ') || "";

                form.setFieldsValue({
                    magdurAdi: adi,
                    magdurSoyadi: soyadi
                });
                message.success("Mağdur vergi kimlik bilgisi başarıyla getirildi");
            }

        } catch (error) {
            console.error("Mağdur Kimlik Sorgulama Hatası:", error);
            message.warning("Mağdur kimlik bilgisi alınamadı");
            form.setFieldsValue({
                magdurKimlikNo: ""
            });
        } finally {
            setIsKimlikSorgulaLoading(false);
        }
    };

    // Servis kimlik sorgulama handler
    const handleServisKimlikSorgula = async (kimlikNo) => {
        if (!kimlikNo || kimlikNo.length < 10) {
            return;
        }

        // Karakter uzunluğuna göre kimlik tipini belirle
        let mappedKimlikTipi = null;
        if (kimlikNo.length === 10) {
            mappedKimlikTipi = "VKN"; // Vergi No
        } else if (kimlikNo.length === 11) {
            mappedKimlikTipi = "TCKN"; // TC Kimlik No
        } else {
            return; // Geçersiz uzunluk
        }

        setIsKimlikSorgulaLoading(true);
        try {
            const result = await fetchKimlikSorgula(token, mappedKimlikTipi, kimlikNo);

            // TC Kimlik için
            if (mappedKimlikTipi === "TCKN" && result) {
                const adSoyad = `${result.adi || ""} ${result.soyadi || ""}`.trim();
                form.setFieldsValue({
                    servisAdSoyad: adSoyad
                });
                message.success("Servis TC kimlik bilgisi başarıyla getirildi");
            }
            // Vergi No için
            else if (mappedKimlikTipi === "VKN" && result) {
                form.setFieldsValue({
                    servisAdSoyad: result.unvan || ""
                });
                message.success("Servis vergi kimlik bilgisi başarıyla getirildi");
            }

        } catch (error) {
            console.error("Servis Kimlik Sorgulama Hatası:", error);
            message.warning("Servis kimlik bilgisi alınamadı");
            form.setFieldsValue({
                servisVergiNo: ""
            });
        } finally {
            setIsKimlikSorgulaLoading(false);
        }
    };

    // Magdur türü değiştiğinde kıllanacak alanları temizle
    useEffect(() => {
        if (magdurTuru === "Mal") {
            form.setFieldsValue({
                magdurAdi: "",
                magdurSoyadi: "",
                magdurCepTelefonu: "",
                magdurEmail: "",
                magdurTescilSeri: "",
                magdurTescilSira: "",
                magdurPlakaIl: "",
                magdurPlaka: "",
                magdurMotorNo: "",
                magdurSasiNo: ""
            });
        }
    }, [magdurTuru]);

    // Kaza tipi değiştiğinde teminat kodunu ayarla
    useEffect(() => {
        const secilen = kazaTipleri.find(x => x.ACIKLAMA === kazaTipi);
        if (secilen) {
            setTeminatKodu(secilen.TeminatKodu);
            form.setFieldsValue({ teminatKodu: secilen.TeminatKodu });
        }
    }, [kazaTipi]);

    const handleServisTipiChange = (value) => {
        setSelectedServisTipi(value);
        // Servis tipi değiştiğinde tamirhane bilgilerini sıfırla
        if (value === "anlasmasiz") {
            setIsTamirhaneLoaded(false);
            form.setFieldsValue({
                servisKodu: "",
                servisAdSoyad: "",
                servisIlKodu: undefined,
                servisIlceKodu: undefined,
                servisAdres: "",
                servisTelefon: "",
                servisVergiNo: ""
            });
        }
    };
    // SMS Doğrulama handler'ları
    const handleSendSmsVerification = async () => {
        try {
            // Önce ilgili form alanını validate et
            await form.validateFields(['ihbarCep']);

            const phoneNumber = form.getFieldValue('ihbarCep');

            // Değerin varlığını ve içeriğini kontrol et
            if (!phoneNumber || phoneNumber.trim() === '' || phoneNumber.replace(/[^\d]/g, '').length === 0) {
                message.error("Lütfen cep telefonu numarasını giriniz");
                return;
            }

            const cleaned = phoneNumber.replace(/[^\d]/g, '');
            if (cleaned.length !== 11 || !cleaned.startsWith('05')) {
                message.error("Geçerli bir cep telefonu numarası giriniz");
                return;
            }

            setIsSendingSms(true);
            try {
                const result = await sendSmsVerification(token, cleaned);
                if (result.success) {
                    message.success("Doğrulama kodu gönderildi");
                    setShowVerificationInput(true);
                    // API'den gelen geçerlilik süresini saniyeye çevir (dakika * 60), boşsa 300 saniye (5 dakika)
                    const gecerlilikSaniye = result.data?.gecerlilikSuresi ? result.data.gecerlilikSuresi * 60 : 300;
                    setCountdown(gecerlilikSaniye);
                    setCanResendSms(false);
                } else {
                    message.error(result.message || "SMS gönderilemedi");
                }
            } catch (error) {
                console.error("SMS gönderme hatası:", error);
                message.error("SMS gönderilemedi");
            } finally {
                setIsSendingSms(false);
            }
        } catch (validationError) {
            // Form validasyon hatası
            console.error("Validation hatası:", validationError);
            message.error("Lütfen geçerli bir cep telefonu numarası giriniz");
        }
    };

    const handleVerifySmsCode = async () => {
        const phoneNumber = form.getFieldValue('ihbarCep');
        const cleaned = phoneNumber.replace(/[^\d]/g, '');

        if (!verificationCode || verificationCode.length !== 4) {
            message.error("Lütfen 4 haneli doğrulama kodunu giriniz");
            return;
        }

        setIsVerifyingSms(true);
        try {
            const result = await verifySmsCode(token, cleaned, verificationCode);
            if (result.success && result.data?.isValid) {
                message.success("Telefon numarası doğrulandı");
                setIsSmsVerified(true);
                setShowVerificationInput(false);
                setVerificationCode("");
                setCountdown(0);
            } else {
                const remainingAttempts = result.data?.remainingAttempts;
                if (remainingAttempts !== undefined) {
                    message.error(`Doğrulama kodu hatalı. Kalan deneme hakkı: ${remainingAttempts}`);
                } else {
                    message.error(result.message || "Doğrulama kodu hatalı");
                }
                if (remainingAttempts === 0) {
                    setShowVerificationInput(false);
                    setVerificationCode("");
                    setCountdown(0);
                    setCanResendSms(true);
                }
            }
        } catch (error) {
            console.error("SMS doğrulama hatası:", error);
            message.error("SMS doğrulaması yapılamadı");
        } finally {
            setIsVerifyingSms(false);
        }
    };

    return (
        <div className="bildirim-container">
            <h2 className="bildirim-title">TRAFİK Hasar İhbar Bildirimi</h2>
            <ConfigProvider locale={trTR}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    autoComplete="off"
                    scrollToFirstError={{
                        behavior: 'smooth',
                        block: 'center',
                    }}
                >
                    <Collapse defaultActiveKey={["1", "2", "3", "4", "5", "6", "7"]} bordered={true} className="bildirim-collapse">
                        <Panel header="Hasar Bilgileri" key="1">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Poliçe Numarası" name="policeNo" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input disabled />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar Nedeni" name="hasarNedeni" rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}>
                                            {sortedHasarNedenleri.map(item => (
                                                <Option key={item.HASAR_KOD} value={item.HASAR_KOD}>
                                                    {item.ACIKLAMA}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="teminatKodu" style={{ display: "none" }}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar Tarihi ve Saati" name="hasarTarihi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>

                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar İli" name="hasarIl" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            loading={isIlLoading}
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}

                                            onChange={val => {
                                                if (val === "0") {
                                                    message.warning({
                                                        content: "Yurtdışında oluşan hasar için dosyanızı Çağrı Merkezini arayarak açtırabilirsiniz",
                                                        duration: 5,
                                                    });
                                                    return;
                                                }
                                                setSelectedIl(val);
                                                form.setFieldsValue({ hasarIlce: undefined });
                                                // İlçeleri yükle
                                                loadHasarIlceListesi(val);
                                            }}
                                        >
                                            {ilListesiState.map(il => (
                                                <Option key={il.ilKod || il.ilKodu} value={il.ilKod || il.ilKodu}>
                                                    {il.ilAdi || il.ilAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar İlçesi" name="hasarIlce" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            loading={isHasarIlceLoading}
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            disabled={!selectedIl}
                                        >
                                            {filteredIlceler.map(ilce => (
                                                <Option key={ilce.ilceKod || ilce.ilceKodu} value={ilce.ilceKod || ilce.ilceKodu}>
                                                    {ilce.ilceAdi || ilce.ilceAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Tahmini Hasar Tutarı"
                                        name="tahminiTutar"
                                        validateTrigger={['onChange', 'onBlur']}
                                        rules={[
                                            { required: true, message: "Tahmini tutarı giriniz" },
                                            {
                                                validator: (_, value) => {
                                                    if (value !== undefined && value !== null && value < 1000) {
                                                        return Promise.reject(new Error('Tahmini Hasar Tutarı 1000TL altında girilemez.'));
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: "100%" }}
                                            formatter={value => {
                                                if (!value) return '';
                                                const parts = value.toString().split('.');
                                                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                                                return parts.length > 1 ? `${parts[0]},${parts[1].slice(0, 2)}` : parts[0];
                                            }}
                                            parser={value => {
                                                if (!value) return '';
                                                return value.replace(/\./g, '').replace(',', '.');
                                            }}
                                            decimalSeparator=","
                                            precision={2}
                                            step={0.01}
                                            min={0}
                                            max={999999999.99}
                                            placeholder="Tutarı TL olarak giriniz (örn: 5.000,50)"
                                            controls={false}
                                            onBlur={() => {
                                                const value = form.getFieldValue('tahminiTutar');
                                                if (value) {
                                                    const rounded = Math.round(value * 100) / 100;
                                                    form.setFieldValue('tahminiTutar', rounded);
                                                    if (value < 1000) {
                                                        message.error("Tahmini Hasar Tutarı 1000TL altında girilemez.");
                                                    }
                                                }
                                                // Validasyonu manuel tetikle
                                                form.validateFields(['tahminiTutar']);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Dosya Türü" name="dosyaTuru" rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                        >
                                            <Option value="Zabit">Zabıt</Option>
                                            <Option value="AnlaşmalıTutanakKTT">Anlaşmalı Tutanak (KTT)</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Hasar Oluş Nedeni"
                                        name="hasarOlusNedeni"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}
                                    >
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            onChange={value => {
                                                const secilen = hasarOlusNedenleri.find(x => x.KOD === value);
                                                if (secilen) {
                                                    setMagdurTuru(secilen.MagdurTuru);
                                                }
                                                setHasarOlusNedeni(value); // Yeni satır

                                            }}
                                        >
                                            {hasarOlusNedenleri.map(item => (
                                                <Option key={item.KOD} value={item.KOD}>
                                                    {item.ACIKLAMA}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="İkili Kaza mı Zincirleme mi?" name="kazaTipi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Radio.Group
                                            onChange={e => {
                                                const value = e.target.value;
                                                const secilen = kazaTipleri.find(x => x.KOD === value);
                                                if (secilen) {
                                                    setKazaTipi(secilen.ACIKLAMA);
                                                    // Teminat kodunu güncelle
                                                    setTeminatKodu(secilen.TeminatKodu);
                                                    form.setFieldsValue({ teminatKodu: secilen.TeminatKodu });
                                                }
                                            }}
                                        >
                                            {kazaTipleri.map(item => (
                                                <Radio key={item.KOD} value={item.KOD}>
                                                    {item.ACIKLAMA}
                                                </Radio>
                                            ))}
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>


                            </Row>
                        </Panel>

                        <Panel header="İhbar Eden Bilgileri" key="2">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="İhbar Eden Tipi"
                                        name="ihbarEdenTipi"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            placeholder="İhbar eden tipini seçiniz"
                                        >
                                            {ihbarEdenTipleri.map(item => (
                                                <Option key={item.KOD} value={item.KOD}>
                                                    {item.ACIKLAMA}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="İhbar Eden Kimlik Tipi"
                                        name="ihbarEdenKimlikTipi"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                            placeholder="Kimlik tipini seçiniz"
                                            onChange={(value) => {
                                                if (value === "3") {
                                                    message.info({
                                                        content: "Pasaport numarası sadece rakam olarak girilmelidir",
                                                        duration: 4,
                                                    });
                                                }

                                                // Kimlik tipi değiştiğinde Kimlik No ve Ad Soyad alanlarını temizle
                                                form.setFieldsValue({
                                                    ihbarEdenKimlikNo: "",
                                                    ihbarAdSoyad: ""
                                                });
                                            }}
                                        >
                                            {ihbarEdenKimlikTipleri.map(item => (
                                                <Option key={item.KOD} value={item.KOD}>
                                                    {item.ACIKLAMA}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="İhbar Eden Kimlik No"
                                        name="ihbarEdenKimlikNo"
                                        rules={[

                                            {
                                                required: true,
                                                validator: (_, value) => {
                                                    const kimlikTipi = form.getFieldValue('ihbarEdenKimlikTipi');

                                                    // Kimliksiz seçiliyse zorunlu değil
                                                    if (kimlikTipi === "9") {
                                                        return Promise.resolve();
                                                    }

                                                    // Diğer durumlarda zorunlu
                                                    if (!value) {
                                                        return Promise.reject(new Error('Bu alan zorunludur'));
                                                    }

                                                    // Pasaport için sadece rakam kontrolü
                                                    if (kimlikTipi === "3") {
                                                        const onlynumbers = /^[0-9]+$/;
                                                        if (!onlynumbers.test(value)) {
                                                            return Promise.reject(new Error('Pasaport numarası sadece rakam olarak girilmelidir'));
                                                        }
                                                    }

                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}

                                    >
                                        <Input
                                            maxLength={11}
                                            disabled={form.getFieldValue('ihbarEdenKimlikTipi') === "9"}
                                            onKeyPress={(e) => {
                                                const kimlikTipi = form.getFieldValue('ihbarEdenKimlikTipi');
                                                if (kimlikTipi === "3") {
                                                    const charCode = e.which ? e.which : e.keyCode;
                                                    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                                                        e.preventDefault();
                                                    }
                                                }
                                            }}
                                            onBlur={async (e) => {
                                                const kimlikNo = e.target.value;
                                                const kimlikTipi = form.getFieldValue('ihbarEdenKimlikTipi');

                                                // TC Kimlik, Vergi No veya Yabancı Kimlik seçiliyse API'yi çağır
                                                if (kimlikNo && (kimlikTipi === "1" || kimlikTipi === "2" || kimlikTipi === "4")) {
                                                    await handleKimlikSorgula(kimlikTipi, kimlikNo);
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            <span>
                                                Adı ve Soyadı / Ünvan
                                                {isKimlikSorgulaLoading && <span style={{ marginLeft: 8, color: '#1890ff' }}>(Sorgulanıyor...)</span>}
                                            </span>
                                        }
                                        name="ihbarAdSoyad"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>

                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={(
                                            <span>
                                                Cep Telefonu
                                                {isSmsVerified && (
                                                    <span style={{ marginLeft: 8, color: '#52c41a', fontSize: '12px' }}>
                                                        ✓ Doğrulandı
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                        required
                                    >
                                        <div style={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
                                            <Form.Item
                                                name="ihbarCep"
                                                noStyle
                                                rules={[
                                                    { required: true, message: "Cep telefonu giriniz" },
                                                    {
                                                        validator: (_, value) => {
                                                            if (!value) return Promise.resolve();
                                                            const cleaned = value.replace(/[^\d]/g, '');
                                                            if (cleaned.length === 11 && cleaned.startsWith('05')) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(new Error('Cep telefonu 05 ile başlamalı ve 11 haneli olmalıdır'));
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputMask
                                                    mask="0(599) 999 99 99"
                                                    maskChar={null}
                                                    formatChars={{
                                                        '9': '[0-9]',
                                                        '5': '[0-9]'
                                                    }}
                                                    disabled={isSmsVerified}
                                                >
                                                    {(inputProps) => (
                                                        <Input
                                                            {...inputProps}
                                                            placeholder="0(5__) ___ __ __"
                                                            maxLength={16}
                                                            style={{
                                                                width: isSmsVerified ? '100%' : 'calc(100% - 90px)',
                                                                borderTopRightRadius: isSmsVerified ? undefined : 0,
                                                                borderBottomRightRadius: isSmsVerified ? undefined : 0
                                                            }}
                                                            onKeyPress={(e) => {
                                                                const value = e.currentTarget.value.replace(/[^\d]/g, '');
                                                                if (value.length >= 11) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </InputMask>
                                            </Form.Item>
                                            {!isSmsVerified && (
                                                <Button
                                                    type="primary"
                                                    style={{
                                                        width: '90px',
                                                        minWidth: '90px',
                                                        maxWidth: '90px',
                                                        borderTopLeftRadius: 0,
                                                        borderBottomLeftRadius: 0,
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}
                                                    onClick={handleSendSmsVerification}
                                                    loading={isSendingSms}
                                                    disabled={!canResendSms || countdown > 0}
                                                >
                                                    {countdown > 0 ? `${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}` : 'Doğrula'}
                                                </Button>
                                            )}
                                        </div>
                                    </Form.Item>
                                    {showVerificationInput && !isSmsVerified && (
                                        <Form.Item
                                            label="Doğrulama Kodu"
                                            help={<span style={{ fontVariantNumeric: 'tabular-nums' }}>{`Kalan süre: ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}`}</span>}
                                        >
                                            <div style={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
                                                <Input
                                                    value={verificationCode}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^\d]/g, '');
                                                        if (value.length <= 4) {
                                                            setVerificationCode(value);
                                                        }
                                                    }}
                                                    placeholder="4 haneli kodu giriniz"
                                                    maxLength={4}
                                                    style={{ width: 'calc(100% - 90px)', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                                    onPressEnter={handleVerifySmsCode}
                                                />
                                                <Button
                                                    type="primary"
                                                    style={{
                                                        width: '90px',
                                                        minWidth: '90px',
                                                        maxWidth: '90px',
                                                        borderTopLeftRadius: 0,
                                                        borderBottomLeftRadius: 0
                                                    }}
                                                    onClick={handleVerifySmsCode}
                                                    loading={isVerifyingSms}
                                                    disabled={verificationCode.length !== 4}
                                                >
                                                    Onayla
                                                </Button>
                                            </div>
                                        </Form.Item>
                                    )}
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Email"
                                        name="ihbarEmail"

                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                type: 'email',
                                                message: 'Geçerli bir email adresi giriniz'
                                            }
                                        ]}
                                    >
                                        <Input placeholder="ornek@email.com" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header="Sürücü Bilgileri (Zurich Sigorta)" key="3">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Kazayı Yapan Kim?" name="surucuKim" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            onChange={async (value) => {
                                                if (value == "Sigortali") {
                                                    try {
                                                        const data = await fetchSigortaliBilgisi(policeNo, token);
                                                        if (data.tcKimlikNo != null && data.tcKimlikNo != "") {
                                                            const phoneNumber = data.gsm || data.tel || "";
                                                            const hasMask = phoneNumber.includes('*');
                                                            setIsMaskedPhone(hasMask);

                                                            setDisableKimlikTipi(!!data.tcKimlikNo);
                                                            setDisableKimlikNo(!!data.tcKimlikNo);
                                                            setDisableSurucuAdi(!!data.mustAdi);
                                                            setDisableSurucuSoyad(!!data.mustSoyadi);
                                                            setDisableSurucuCep(!!phoneNumber);
                                                            setDisableSurucuEmail(!!data.eMail);
                                                            setDisableSurucuYas(!!data.yas);

                                                            form.setFieldsValue({
                                                                kimlikTipi: data.tcKimlikNo ? "TcKimlikNo" : "",
                                                                kimlikNo: data.tcKimlikNo || "",
                                                                surucuAdi: data.mustAdi || "",
                                                                surucuSoyad: data.mustSoyadi || "",
                                                                surucuCep: phoneNumber,
                                                                surucuEmail: data.eMail || "",
                                                                surucuYas: data.yas || ""
                                                            });
                                                        }
                                                    } catch (err) {
                                                        message.error("Sigortalı bilgisi alınamadı");
                                                        setDisableKimlikTipi(false);
                                                        setDisableKimlikNo(false);
                                                        setDisableSurucuAdi(false);
                                                        setDisableSurucuSoyad(false);
                                                        setDisableSurucuCep(false);
                                                        setDisableSurucuEmail(false);
                                                        setDisableSurucuYas(false);
                                                    }
                                                } else {
                                                    setIsMaskedPhone(false);
                                                    setDisableKimlikTipi(false);
                                                    setDisableKimlikNo(false);
                                                    setDisableSurucuAdi(false);
                                                    setDisableSurucuSoyad(false);
                                                    setDisableSurucuCep(false);
                                                    setDisableSurucuEmail(false);
                                                    setDisableSurucuYas(false);
                                                    form.setFieldsValue({
                                                        kimlikTipi: "",
                                                        kimlikNo: "",
                                                        surucuAdi: "",
                                                        surucuSoyad: "",
                                                        surucuCep: "",
                                                        surucuEmail: "",
                                                        surucuYas: ""
                                                    });
                                                }
                                            }}
                                        >
                                            <Option value="Sigortali">Sigortalı</Option>
                                            <Option value="Aile">Ailesi</Option>
                                            <Option value="Diger">Diğer</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Kimlik Tipi" name="kimlikTipi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            disabled={disableKimlikTipi}
                                            onChange={(value) => {
                                                // Pasaport seçildiğinde bilgilendirme mesajı göster
                                                if (value === "PasaportNo") {
                                                    message.info({
                                                        content: "Pasaport numarası sadece rakam olarak girilmelidir",
                                                        duration: 4,
                                                    });
                                                }

                                                // Kimlik tipi değiştiğinde Kimlik No, Ad ve Soyad alanlarını temizle
                                                if (!disableKimlikTipi) {
                                                    form.setFieldsValue({
                                                        kimlikNo: "",
                                                        surucuAdi: "",
                                                        surucuSoyad: ""
                                                    });
                                                }
                                            }}
                                        >
                                            <Option value="TcKimlikNo">TC Kimlik Numarası</Option>
                                            <Option value="YabanciKimlikNo">Yabancı Kimlik Numarası</Option>
                                            <Option value="PasaportNo">Pasaport Numarası</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Kimlik No"
                                        name="kimlikNo"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    const kimlikTipi = form.getFieldValue('kimlikTipi');
                                                    if (!value) return Promise.resolve();

                                                    // Pasaport numarası için sadece rakam kontrolü
                                                    if (kimlikTipi === "PasaportNo") {
                                                        const onlynumbers = /^[0-9]+$/;
                                                        if (!onlynumbers.test(value)) {
                                                            return Promise.reject(new Error('Pasaport numarası sadece rakam olarak girilmelidir'));
                                                        }
                                                    }

                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <Input
                                            maxLength={11}
                                            disabled={disableKimlikNo}
                                            onKeyPress={(e) => {
                                                const kimlikTipi = form.getFieldValue('kimlikTipi');
                                                // Pasaport seçili ise sadece rakam girişine izin ver
                                                if (kimlikTipi === "PasaportNo") {
                                                    const charCode = e.which ? e.which : e.keyCode;
                                                    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                                                        e.preventDefault();
                                                    }
                                                }
                                            }}
                                            onBlur={async (e) => {
                                                const kimlikNo = e.target.value;
                                                const kimlikTipi = form.getFieldValue('kimlikTipi');

                                                // Disabled değilse ve TC Kimlik, Vergi No veya Yabancı Kimlik seçiliyse API'yi çağır
                                                if (!disableKimlikNo && kimlikNo) {
                                                    let apiKimlikTipi = null;

                                                    if (kimlikTipi === "TcKimlikNo") {
                                                        apiKimlikTipi = "1"; // TC Kimlik
                                                    } else if (kimlikTipi === "YabanciKimlikNo") {
                                                        apiKimlikTipi = "4"; // Yabancı Kimlik
                                                    }

                                                    if (apiKimlikTipi) {
                                                        await handleSurucuKimlikSorgula(apiKimlikTipi, kimlikNo);
                                                    }
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            <span>
                                                Sürücü Adı
                                                {isKimlikSorgulaLoading && <span style={{ marginLeft: 8, color: '#1890ff' }}>(Sorgulanıyor...)</span>}
                                            </span>
                                        }
                                        name="surucuAdi"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Input disabled={disableSurucuAdi} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Sürücü Soyadı" name="surucuSoyad" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input disabled={disableSurucuSoyad} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Sürücü Cep Telefonu"
                                        name="surucuCep"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    if (isMaskedPhone) return Promise.resolve();
                                                    const cleaned = value.replace(/[^\d]/g, '');
                                                    if (cleaned.length === 11 && cleaned.startsWith('05')) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Cep telefonu 05 ile başlamalı ve 11 haneli olmalıdır'));
                                                }
                                            }
                                        ]}
                                    >
                                        {isMaskedPhone ? (
                                            <Input disabled placeholder="Maskeli telefon" />
                                        ) : (
                                            <InputMask
                                                mask="0(599) 999 99 99"
                                                maskChar={null}
                                                formatChars={{
                                                    '9': '[0-9]',
                                                    '5': '[0-9]'
                                                }}
                                                disabled={disableSurucuCep}
                                            >
                                                {(inputProps) => (
                                                    <Input
                                                        {...inputProps}
                                                        placeholder="0(5__) ___ __ __"
                                                        maxLength={16}
                                                        onKeyPress={(e) => {
                                                            const value = e.currentTarget.value.replace(/[^\d]/g, '');
                                                            if (value.length >= 11) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </InputMask>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Sürücü Email" name="surucuEmail">
                                        <Input disabled={disableSurucuEmail} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Kazayı Yapan Kişinin Yaşı" name="surucuYas">
                                        <Input disabled={disableSurucuYas} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header="Servis Bilgileri" key="4">
                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item label="Servis Tipi" name="servisTipi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Radio.Group onChange={(e) => {
                                            const value = e.target.value;
                                            setSelectedServisTipi(value);
                                            // Servis tipi değiştiğinde tamirhane bilgilerini sıfırla
                                            if (value === "anlasmasiz") {
                                                setIsTamirhaneLoaded(false);
                                                form.setFieldsValue({
                                                    servisKodu: "",
                                                    servisAdSoyad: "",
                                                    servisIlKodu: undefined,
                                                    servisIlceKodu: undefined,
                                                    servisAdres: "",
                                                    servisTelefon: "",
                                                    servisVergiNo: ""
                                                });
                                            }
                                        }}>
                                            <Radio value="anlasmali">Anlaşmalı Servis</Radio>
                                            <Radio value="anlasmasiz">Anlaşmasız Servis</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            selectedServisTipi === "anlasmali" ? (
                                                <span>
                                                    Servis Kodu&nbsp;
                                                    <Tooltip title="hasarlojistik@zurich.com mail adresinden tamirhane kodunuzu öğrenebilirsiniz.">
                                                        <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                    </Tooltip>
                                                </span>
                                            ) : (
                                                "Servis Kodu"
                                            )
                                        }
                                        name="servisKodu"
                                        rules={[
                                            {
                                                required: selectedServisTipi === "anlasmali",
                                                message: "Anlaşmalı servis için servis kodu zorunludur"
                                            }
                                        ]}
                                    >
                                        <Input
                                            disabled={selectedServisTipi === "anlasmasiz"}
                                            onBlur={async (e) => {
                                                const tamirhaneKod = e.target.value;
                                                if (tamirhaneKod && selectedServisTipi === "anlasmali") {
                                                    try {
                                                        const response = await fetchTamirhaneBilgisi(token, tamirhaneKod);
                                                        const data = Array.isArray(response) ? response[0] : response;
                                                        if (data) {
                                                            // Önce il kodunu set et ve selected state'i güncelle
                                                            setSelectedServisIl(data.ilKod || undefined);

                                                            // İlçe listesini yükle
                                                            if (data.ilKod) {
                                                                await loadServisIlceListesi(data.ilKod);
                                                            }

                                                            // İlçe listesi yüklendikten SONRA tüm değerleri set et
                                                            form.setFieldsValue({
                                                                servisAdSoyad: data.adSoyadUnvan || "",
                                                                servisIlKodu: data.ilKod || undefined,
                                                                servisIlceKodu: data.ilceKod || undefined,
                                                                servisAdres: data.adres || "",
                                                                servisTelefon: data.telefon || "",
                                                                servisVergiNo: data.vergiNo || "1"
                                                            });

                                                            setIsTamirhaneLoaded(true);
                                                        }
                                                    } catch (err) {
                                                        message.error("Tamirhane bilgisi alınamadı");
                                                        // Tamirhane bilgisi alınamazsa alanları boşalt
                                                        form.setFieldsValue({
                                                            servisAdSoyad: "",
                                                            servisIlKodu: undefined,
                                                            servisIlceKodu: undefined,
                                                            servisAdres: "",
                                                            servisTelefon: "",
                                                            servisVergiNo: ""
                                                        });
                                                        setSelectedServisIl(undefined);
                                                        setIsTamirhaneLoaded(false);
                                                    }
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            <span>
                                                Tckn/Vergi Numarası
                                                {isKimlikSorgulaLoading && <span style={{ marginLeft: 8, color: '#1890ff' }}>(Sorgulanıyor...)</span>}
                                            </span>
                                        }
                                        name="servisVergiNo"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    // 10 veya 11 karakter olmalı
                                                    if (value.length === 10 || value.length === 11) {
                                                        return Promise.resolve();
                                                    }
                                                    if (selectedServisTipi != "anlasmali") {
                                                        return Promise.reject(new Error('TC Kimlik 11, Vergi No 10 haneli olmalıdır'));
                                                    } else {
                                                        return Promise.resolve();
                                                    }
                                                }
                                            }
                                        ]}
                                    >
                                        <Input
                                            maxLength={11}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                            onBlur={async (e) => {
                                                const kimlikNo = e.target.value;

                                                // Anlaşmasız servis ve tamirhane yüklü değilse API'yi çağır
                                                if (selectedServisTipi === "anlasmasiz" && !isTamirhaneLoaded && kimlikNo) {
                                                    await handleServisKimlikSorgula(kimlikNo);
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Ad/Soyad/Ünvan" name="servisAdSoyad" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Telefon"
                                        name="servisTelefon"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    const cleaned = value.replace(/[^\d]/g, '');
                                                    if (cleaned.length === 11 || cleaned.length === 10) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Telefon 10 veya 11 haneli olmalıdır'));
                                                }
                                            }
                                        ]}
                                    >
                                        <InputMask
                                            mask="0(999) 999 99 99"
                                            maskChar={null}
                                            formatChars={{
                                                '9': '[0-9]'
                                            }}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                        >
                                            {(inputProps) => (
                                                <Input
                                                    {...inputProps}
                                                    placeholder="0(___) ___ __ __"
                                                    maxLength={16}
                                                    onKeyPress={(e) => {
                                                        const value = e.currentTarget.value.replace(/[^\d]/g, '');
                                                        if (value.length >= 11) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                            )}
                                        </InputMask>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="İl" name="servisIlKodu" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                            onChange={val => {
                                                setSelectedServisIl(val);
                                                form.setFieldsValue({ servisIlceKodu: undefined });
                                                // İlçeleri yükle
                                                loadServisIlceListesi(val);
                                            }}
                                        >
                                            {ilListesiState.map(il => (
                                                <Option key={il.ilKod || il.ilKodu} value={il.ilKod || il.ilKodu}>
                                                    {il.ilAdi || il.ilAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="İlçe" name="servisIlceKodu" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            loading={isServisIlceLoading}
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                            disabled={!selectedServisIl || (selectedServisTipi === "anlasmali" && isTamirhaneLoaded)}
                                        >
                                            {filteredServisIlceler.map(ilce => (
                                                <Option key={ilce.ilceKod || ilce.ilceKodu} value={ilce.ilceKod || ilce.ilceKodu}>
                                                    {ilce.ilceAdi || ilce.ilceAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Açık Adres" name="servisAdres" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input.TextArea
                                            rows={3}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header="Mağdur Bilgileri (Ruhsat Sahibi)" key="5">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Kimlik Tipi"
                                        name="magdurKimlikTipi"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]
                                        }
                                    >
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            placeholder="Kimlik tipini seçiniz"
                                            onChange={(value) => {
                                                if (value === "Yabancı") {
                                                    message.info({
                                                        content: "Yabancı kimlik için kimlik numarası girilmelidir",
                                                        duration: 4,
                                                    });
                                                }

                                                // Kimlik tipi değiştiğinde Kimlik No, Adı ve Soyadı temizle
                                                form.setFieldsValue({
                                                    magdurKimlikNo: "",
                                                    magdurAdi: "",
                                                    magdurSoyadi: ""
                                                });
                                            }}
                                        >
                                            <Option value="Bireysel">Bireysel</Option>
                                            <Option value="Ticari">Ticari</Option>
                                            <Option value="Yabancı">Yabancı</Option>

                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Kimlik No"
                                        name="magdurKimlikNo"
                                        rules={[
                                            {
                                                required: true,
                                                validator: (_, value) => {
                                                    const kimlikTipi = form.getFieldValue('magdurKimlikTipi');

                                                    if (!value) {
                                                        return Promise.reject(new Error('Bu alan zorunludur'));
                                                    }
                                                    if (kimlikTipi === "Yabancı") {
                                                        const onlynumbers = /^[0-9]+$/;
                                                        if (!onlynumbers.test(value)) {
                                                            return Promise.reject(new Error('Pasaport numarası sadece rakam olarak girilmelidir'));
                                                        }
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <Input
                                            maxLength={11}
                                            onBlur={async (e) => {
                                                const kimlikNo = e.target.value;
                                                const kimlikTipi = form.getFieldValue('magdurKimlikTipi');

                                                // TC Kimlik, Vergi No veya Yabancı Kimlik seçiliyse API'yi çağır
                                                if (kimlikNo && (kimlikTipi === "Bireysel" || kimlikTipi === "Ticari" || kimlikTipi === "Yabancı")) {
                                                    await handleMagdurKimlikSorgula(kimlikTipi, kimlikNo);
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            <span>
                                                Adı
                                                {isKimlikSorgulaLoading && <span style={{ marginLeft: 8, color: '#1890ff' }}>(Sorgulanıyor...)</span>}
                                            </span>
                                        }
                                        name="magdurAdi"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}>

                                        <Input placeholder="Adını giriniz" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Soyadı" name="magdurSoyadi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input placeholder="Soyadını giriniz" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Cep Telefonu"
                                        name="magdurCepTelefonu"
                                        rules={[
                                            { required: true, message: "Cep telefonu giriniz" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    const cleaned = value.replace(/[^\d]/g, '');
                                                    if (cleaned.length === 11 && cleaned.startsWith('05')) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Cep telefonu 05 ile başlamalı ve 11 haneli olmalıdır'));
                                                }
                                            }
                                        ]}
                                    >
                                        <InputMask
                                            mask="0(599) 999 99 99"
                                            maskChar={null}
                                            formatChars={{
                                                '9': '[0-9]',
                                                '5': '[0-9]'
                                            }}
                                        >
                                            {(inputProps) => (
                                                <Input
                                                    {...inputProps}
                                                    placeholder="0(5__) ___ __ __"
                                                    maxLength={16}
                                                />
                                            )}
                                        </InputMask>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Email"
                                        name="magdurEmail"
                                        rules={[
                                            {
                                                type: 'email',
                                                message: 'Geçerli bir email adresi giriniz'
                                            }
                                        ]}
                                    >
                                        <Input placeholder="ornek@email.com" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={4}>
                                    <Form.Item
                                        label={
                                            <span>
                                                Tescil Belge Seri Kod&nbsp;
                                                <Tooltip title="Tescil Belge Seri Numarası, ruhsatın sağ alt köşesinde yer almaktadır.">
                                                    <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                </Tooltip>
                                            </span>
                                        }
                                        name="magdurTescilSeri"
                                        rules={[
                                            {
                                                required: hasarOlusNedeni === "1",
                                                message: "Bu alan zorunludur"
                                            }
                                        ]}
                                    >
                                        <Input
                                            placeholder="Kod"
                                            maxLength={3}
                                            disabled={isTramerDataLoaded || hasarOlusNedeni !== "1"}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                form.setFieldsValue({ magdurTescilSeri: value });
                                            }}
                                            style={{ textTransform: 'uppercase' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="Seri No"
                                        name="magdurTescilSira"
                                        rules={[
                                            {
                                                required: hasarOlusNedeni === "1",
                                                message: "Bu alan zorunludur"
                                            }
                                        ]}
                                    >
                                        <Input
                                            placeholder="No"
                                            maxLength={6}
                                            disabled={isTramerDataLoaded || hasarOlusNedeni !== "1"}
                                        />
                                    </Form.Item>

                                </Col>
                                <Col xs={24} md={4}>
                                    <Form.Item
                                        label={
                                            <span>
                                                Plaka İl Kodu
                                            </span>
                                        }
                                        name="magdurPlakaIl"
                                        rules={[
                                            {
                                                required: hasarOlusNedeni === "1",
                                                message: "Bu alan zorunludur"
                                            }
                                        ]}
                                    >
                                        <Input
                                            placeholder=" 34  (İl Kodu)"
                                            maxLength={3}
                                            disabled={isTramerDataLoaded || hasarOlusNedeni !== "1"}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="Plaka"
                                        name="magdurPlaka"
                                        rules={[
                                            {
                                                required: hasarOlusNedeni === "1",
                                                message: "Bu alan zorunludur"
                                            }
                                        ]}
                                    >
                                        <Input
                                            placeholder="ABC 123"
                                            maxLength={10}
                                            disabled={isTramerDataLoaded || hasarOlusNedeni !== "1"}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                form.setFieldsValue({ magdurPlaka: value });
                                            }}
                                            style={{ textTransform: 'uppercase' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item name="magdurKamuMu" valuePropName="checked">
                                        <Checkbox
                                            onChange={(e) => setKamuMu(e.target.checked)}
                                            disabled={isTramerDataLoaded || hasarOlusNedeni !== "1"}
                                        >
                                            Kamu Mu?
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            onClick={handleTramerSorgula}
                                            loading={isTramerLoading}
                                            icon={<InfoCircleOutlined />}
                                            disabled={isTramerDataLoaded || hasarOlusNedeni !== "1"}
                                        >
                                            Araç Bilgilerini Sorgula
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Marka" name="magdurMarkaKodu">
                                        <Input placeholder="Marka Kod" disabled={isTramerDataLoaded || true} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label=" " name="magdurMarka">
                                        <Input placeholder="Marka Adı" maxLength={3} disabled={isTramerDataLoaded || true} />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={8}>
                                    <Form.Item label="Model Yılı" name="magdurModel">
                                        <Input placeholder="Model yılı giriniz" disabled={isTramerDataLoaded} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={4}>
                                    <div style={{ display: 'none' }}>
                                        <Form.Item label=" " name="magdurModelKodu">
                                            <Input />
                                        </Form.Item>
                                    </div>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Araç Tipi" name="magdurTipi">
                                        <Input placeholder="Tipi" disabled={isTramerDataLoaded} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={4}>
                                    <div style={{ display: 'none' }}>
                                        <Form.Item label=" " name="magdurTipiKodu">
                                            <Input />
                                        </Form.Item>
                                    </div>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Araç Türü" name="magdurTuru">
                                        <Select
                                            allowClear
                                            showSearch
                                            loading={isAracTurleriLoading}
                                            placeholder="Araç türünü seçiniz"
                                            disabled={isTramerDataLoaded}
                                            optionFilterProp="label"
                                            filterOption={turkishSearch}
                                            onChange={(value) => {
                                                const secilen = aracTurleri.find(x => x.kod === value);
                                                if (secilen) {
                                                    form.setFieldsValue({
                                                        magdurTuruKodu: secilen.kod
                                                    });
                                                }
                                                else {
                                                    form.setFieldsValue({
                                                        magdurTuruKodu: ''
                                                    });
                                                }
                                            }}
                                        >
                                            {aracTurleri.map(tur => (
                                                <Option
                                                    key={tur.kod}
                                                    value={tur.kod}
                                                    label={`${tur.kod} - ${tur.aciklama}`}
                                                >
                                                    {tur.aciklama}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={2}>

                                    <Form.Item label=" " name="magdurTuruKodu">
                                        <Input placeholder="Kod" disabled={true} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Motor No" name="magdurMotorNo">
                                        <Input placeholder="Motor numarasını giriniz" disabled={isTramerDataLoaded} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Şasi No" name="magdurSasiNo">
                                        <Input placeholder="Şasi numarasını giriniz" disabled={isTramerDataLoaded} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header="Mağdur Hesap Bilgileri (Ruhsat Sahibi)" key="6">
                            <Alert
                                message="Anlaşmasız Servislerde Hesap Bilgilerinin doldurulmasını rica ederiz."
                                type="warning"
                                showIcon
                                style={{ marginBottom: '16px' }}
                            />
                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item label="IBAN" name="magdurIban">
                                        <Input placeholder="TR__ ____ ____ ____ ____ ____ __" maxLength={32}
                                            autoComplete="new-password"
                                            data-form-type="other"
                                            data-lpignore="true"
                                            readOnly
                                            onFocus={(e) => e.target.removeAttribute('readonly')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Hesap Sahibi Adı" name="magdurHesapSahibiAdi">
                                        <Input placeholder="Adı" autoComplete="new-password"
                                            data-form-type="other"
                                            data-lpignore="true"
                                            readOnly
                                            onFocus={(e) => e.target.removeAttribute('readonly')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Hesap Sahibi Soyadı" name="magdurHesapSahibiSoyadi">
                                        <Input placeholder="Soyadı" autoComplete="new-password"
                                            data-form-type="other"
                                            data-lpignore="true" readOnly
                                            onFocus={(e) => e.target.removeAttribute('readonly')}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>

                        <Panel header="Hak Sahibi Bilgileri" key="7">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Hak Sahibi Adı"
                                        name="hakSahibiAdi"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}
                                    >
                                        <Input placeholder="Hak sahibinin adını giriniz" maxLength={30} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Hak Sahibi Soyadı"
                                        name="hakSahibiSoyadi"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}
                                    >
                                        <Input placeholder="Hak sahibinin soyadını giriniz" maxLength={30} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Hak Sahibi Cep Telefonu"
                                        name="hakSahibiCepTelefonu"
                                        rules={[
                                            { required: true, message: "Cep telefonu giriniz" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    const cleaned = value.replace(/[^\d]/g, '');
                                                    if (cleaned.length === 11 && cleaned.startsWith('05')) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Cep telefonu 05 ile başlamalı ve 11 haneli olmalıdır'));
                                                }
                                            }
                                        ]}
                                    >
                                        <InputMask
                                            mask="0(599) 999 99 99"
                                            maskChar={null}
                                            formatChars={{
                                                '9': '[0-9]',
                                                '5': '[0-9]'
                                            }}
                                        >
                                            {(inputProps) => (
                                                <Input
                                                    {...inputProps}
                                                    placeholder="0(5__) ___ __ __"
                                                    maxLength={16}
                                                />
                                            )}
                                        </InputMask>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Hak Sahibi Email"
                                        name="hakSahibiEmail"
                                        rules={[
                                            {
                                                type: 'email',
                                                message: 'Geçerli bir email adresi giriniz'
                                            }
                                        ]}
                                    >
                                        <Input placeholder="Email adresini giriniz" maxLength={100} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item
                                        label="Hak Sahibi Adres"
                                        name="hakSahibiAdres"
                                        rules={[{ required: true, message: "Bu alan zorunludur" }]}
                                    >
                                        <Input.TextArea
                                            rows={3}
                                            placeholder="Adres bilgisini giriniz"
                                            maxLength={200}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>

                    <div className="bildirim-actions">
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="bildirim-btn"
                            loading={isSubmitting}
                        >
                            Kaydet
                        </Button>
                        <Button
                            htmlType="button"
                            className="bildirim-btn"
                            onClick={() => navigate("/trafik")}
                        >
                            Vazgeç
                        </Button>
                    </div>

                    {errorMessage && (
                        <Alert
                            message="Hata"
                            description={errorMessage}
                            type="error"
                            closable
                            onClose={() => setErrorMessage("")}
                            style={{ marginTop: "16px" }}
                            showIcon
                        />
                    )}
                </Form>
            </ConfigProvider>
        </div >
    );
}

export default TrafikIhbarBildirimPage;
